<template>
  <div class="app" :class="{ scrolled: isScrolled }">

    <!-- LOADING OVERLAY -->
    <HyFLoader v-if="submitting" message="Enviando tu solicitud…" aria-label="Enviando tu solicitud" />

    <!-- HEADER -->
    <header class="header" :class="{ 'header--scrolled': isScrolled }">
      <div class="container header__inner">
        <a class="logo" :href="homeUrl">
          <img class="logo__img" :src="assets.logoHorizontalSrc" alt="House and Flats" />
          <span class="logo__property">Property</span>
        </a>

        <nav class="nav" :class="{ 'nav--open': mobileOpen }">
          <button class="nav__link" @click="scrollTo('como-funciona')">Cómo funciona</button>
          <button class="nav__link" @click="scrollTo('transparencia')">Transparencia</button>
          <button class="btn btn--primary" @click="scrollTo('contacto')">Solicitar evaluación</button>
        </nav>

        <button class="hamburger" @click="mobileOpen = !mobileOpen" :aria-label="mobileOpen ? 'Cerrar menú' : 'Abrir menú'">
          <span class="hamburger__line" :class="{ 'hamburger__line--open1': mobileOpen }"></span>
          <span class="hamburger__line" :class="{ 'hamburger__line--open2': mobileOpen }"></span>
          <span class="hamburger__line" :class="{ 'hamburger__line--open3': mobileOpen }"></span>
        </button>
      </div>

      <div class="mobile-menu" :class="{ 'mobile-menu--open': mobileOpen }">
        <button class="nav__link" @click="scrollTo('como-funciona')">Cómo funciona</button>
        <button class="nav__link" @click="scrollTo('transparencia')">Transparencia</button>
        <button class="btn btn--primary btn--full" @click="scrollTo('contacto')">Solicitar evaluación</button>
      </div>
    </header>

    <!-- HERO -->
    <section id="hero" class="hero">
      <div class="container hero__inner">
        <div class="hero__copy">
          <h1 class="hero__title">
            <b>Convierte tu propiedad en un activo operado, con control y pago mensual.</b>
          </h1>
          <p class="hero__subtitle">
            H&amp;F Property administra tu unidad de punta a punta: distribución multicanal (Airbnb, Booking y corredores tradicionales), estrategia de renta mid-term, operación profesional, reportes claros y pago mensual al propietario.
          </p>
          <ul class="hero__checklist">
            <li v-for="item in heroChecklist" :key="item" class="hero__check-item">
              <svg class="check-icon" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FF6A00" opacity="0.15"/><path d="M8 12.5l2.5 2.5 5.5-5.5" stroke="#FF6A00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span>{{ item }}</span>
            </li>
          </ul>
          <div class="hero__ctas">
            <button class="btn btn--primary btn--lg" @click="scrollTo('contacto')">
              Solicitar evaluación gratuita
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button class="btn btn--outline btn--lg" @click="scrollTo('como-funciona')">
              Ver como funciona
            </button>
          </div>
        </div>

        <div class="hero__dashboard">
          <!-- Santolaya flotando al costado izquierdo del dashboard -->
          <a
            href="https://santolaya.cl/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Santolaya"
            class="santolaya-card"
          >
            <img class="dashboard__logo" :src="santolayaLogo" alt="Santolaya" />
          </a>

          <div class="dashboard">
            <div class="dashboard__header">
              <span class="dashboard__dot"></span>
              <span class="dashboard__label">Liquidación mensual ejemplo</span>
            </div>
            <div class="dashboard__grid">
              <div class="dash-card" v-for="card in dashboardCards" :key="card.label">
                <div class="dash-card__top">
                  <component :is="'svg'" class="dash-icon" viewBox="0 0 24 24" fill="none" v-html="card.iconPath"></component>
                  <span class="dash-card__label">{{ card.label }}</span>
                </div>
                <p class="dash-card__value">{{ card.value }}</p>
                <span class="dash-card__trend" :class="card.special ? 'dash-card__trend--badge' : ''">{{ card.trend }}</span>
              </div>
            </div>
          </div>
          <div class="hero__dashboard-bg"></div>
        </div>
      </div>

      <div class="container">
        <div class="trust-bar">
          <div class="trust-stat" v-for="stat in trustStats" :key="stat.label">
            <p class="trust-stat__value">{{ stat.value }}</p>
            <p class="trust-stat__label">{{ stat.label }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- POSITIONING -->
    <section class="positioning section section--alt">
      <div class="container">
            <div class="section__header">
          <h2 class="section__title"><b>No somos una corredora. Somos un operador de activos inmobiliarios amoblados.</b></h2>
          <p class="section__subtitle">
            Un administrador tradicional cobra por gestionar. Un 'Airbnb manager' se enfoca en reservas. H&amp;F Property opera tu inmueble como un activo: estrategia de ingresos, operación estandarizada y control financiero.
          </p>
        </div>
        <div class="cards-grid cards-grid--3">
          <div class="card" v-for="card in positioningCards" :key="card.title">
            <div class="card__icon-wrap">
              <svg class="card__icon" viewBox="0 0 24 24" fill="none" v-html="card.iconPath"></svg>
            </div>
            <h3 class="card__title">{{ card.title }}</h3>
            <p class="card__desc">{{ card.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- COMPARISON -->
    <section class="comparison section">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title"><b>Mayor optimización de los activos en el tiempo.</b></h2>
        </div>

        <!-- Desktop table -->
        <div class="comparison-table">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Renta tradicional</th>
                <th>Corto plazo (Airbnb)</th>
                <th class="comparison-table__hf">Modelo H&amp;F (Short-mid-long tern)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in comparisonRows" :key="row.label">
                <td class="comparison-table__label">{{ row.label }}</td>
                <td><div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.traditionalIcon"></span>{{ row.traditional }}</div></td>
                <td><div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.shortTermIcon"></span>{{ row.shortTerm }}</div></td>
                <td class="comparison-table__hf"><div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.hfIcon"></span><strong>{{ row.hf }}</strong></div></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="comparison-mobile">
          <div class="comparison-card" v-for="row in comparisonRows" :key="row.label">
            <p class="comparison-card__label">{{ row.label }}</p>
            <div class="comparison-card__cols">
              <div>
                <p class="comparison-card__col-title">Tradicional</p>
                <div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.traditionalIcon"></span>{{ row.traditional }}</div>
              </div>
              <div>
                <p class="comparison-card__col-title">Airbnb</p>
                <div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.shortTermIcon"></span>{{ row.shortTerm }}</div>
              </div>
              <div class="comparison-card__hf">
                <p class="comparison-card__col-title comparison-card__col-title--hf">H&amp;F</p>
                <div class="status-cell"><span class="status-icon" :class="'status-icon--' + row.hfIcon"></span><strong>{{ row.hf }}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section id="como-funciona" class="how-it-works section section--alt">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title"><b>Empieza en 4 pasos</b></h2>
        </div>
        <div class="steps-grid">
          <div class="step" v-for="(step, i) in steps" :key="step.number">
            <div class="step__card">
              <span class="step__number">{{ step.number }}</span>
              <div class="step__icon-wrap">
                <svg class="step__icon" viewBox="0 0 24 24" fill="none" v-html="step.iconPath"></svg>
              </div>
              <h3 class="step__title">{{ step.title }}</h3>
              <p class="step__desc">{{ step.description }}</p>
            </div>
            <div class="step__connector" v-if="i < steps.length - 1"></div>
          </div>
        </div>
        <div class="section__cta">
          <button class="btn btn--primary btn--lg" @click="scrollTo('contacto')">
            Solicitar evaluación gratuita
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>
    </section>

    <!-- METRICS -->
    <section class="metrics section">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title">Análisis interno de métricas.</h2>
        </div>
        <div class="metrics-grid">
          <div class="metric-card" v-for="metric in metrics" :key="metric.label">
            <div class="metric-card__icon-wrap">
              <svg class="metric-card__icon" viewBox="0 0 24 24" fill="none" v-html="metric.iconPath"></svg>
            </div>
            <p class="metric-card__value">{{ metric.value }}</p>
            <p class="metric-card__label">{{ metric.label }}</p>
          </div>
        </div>
        <p class="metrics__note">Métricas referenciales: se ajustan por zona, estacionalidad y estándar del activo.</p>
      </div>
    </section>

    <!-- TRANSPARENCY -->
    <section id="transparencia" class="transparency section section--alt">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title">Transparencia primero.</h2>
        </div>
        <div class="transparency__grid">
          <div class="flow">
            <div class="flow-step flow-step--dark">
              <div>
                <p class="flow-step__label">Revenue</p>
                <p class="flow-step__sub">Ingresos brutos de canales</p>
              </div>
            </div>
            <div class="flow__arrow">↓</div>
            <div class="flow-step flow-step--muted">
              <div>
                <p class="flow-step__label">Costos operativos</p>
                <p class="flow-step__sub">Limpieza, mantenimiento, insumos, comisiones</p>
              </div>
              <span class="flow-step__minus">−</span>
            </div>
            <div class="flow__arrow">↓</div>
            <div class="flow-step flow-step--accent">
              <div>
                <p class="flow-step__label">Ingreso neto</p>
                <p class="flow-step__sub">Revenue menos costos</p>
              </div>
            </div>
            <div class="flow__arrow">↓</div>
            <div class="flow-step flow-step--primary">
              <div>
                <p class="flow-step__label flow-step__label--white">Pago propietario</p>
                <p class="flow-step__sub flow-step__sub--white">Transferencia directa.</p>
              </div>
              <span class="flow-step__arrow">→</span>
            </div>
          </div>

          <div class="transparency__explanation">
            <h3 class="transparency__sub-title">Cómo funciona el flujo económico</h3>
            <ol class="transparency__steps">
              <li v-for="(step, i) in transparencySteps" :key="i">
                <span class="transparency__num">{{ i + 1 }}</span>
                <span>{{ step }}</span>
              </li>
            </ol>
            <div class="net-box">
              <h4 class="net-box__title">¿Qué consideramos 'neto'?</h4>
              <p class="net-box__sub">Se descuentan los siguientes costos operativos:</p>
              <ul class="net-box__list">
                <li v-for="item in netItems" :key="item">
                  <span class="net-box__dot"></span>{{ item }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SOCIAL PROOF -->
    <section class="social-proof section">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title">Elegido por propietarios e inversionistas que valoran control.</h2>
        </div>
        <div class="testimonials">
          <div class="testimonial" v-for="t in testimonials" :key="t.author">
            <div class="stars">
              <svg v-for="s in 5" :key="s" width="16" height="16" viewBox="0 0 24 24"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="#FF6A00" stroke="#FF6A00" stroke-width="1"/></svg>
            </div>
            <blockquote class="testimonial__quote">"{{ t.quote }}"</blockquote>
            <div class="testimonial__footer">
              <p class="testimonial__author">{{ t.author }}</p>
              <p class="testimonial__role">{{ t.role }}</p>
            </div>
          </div>
        </div>

        <div class="partners">
          <div class="partner-badge" v-for="p in partners" :key="p">{{ p }}</div>
        </div>

        <div class="standards">
          <h3 class="standards__title">Estándares de operación</h3>
          <div class="standards__grid">
            <div class="standard-item" v-for="s in standards" :key="s.label">
              <svg class="standard-item__icon" viewBox="0 0 24 24" fill="none" v-html="s.iconPath"></svg>
              <span>{{ s.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT FORM -->
    <section id="contacto" class="contact section section--alt">
      <div class="container">
        <div class="section__header">
          <h2 class="section__title">Recibe una evaluación del activo en 24 horas.</h2>
          <p class="section__subtitle">
            Te enviamos una propuesta con estrategia recomendada, rango de NOI esperado y condiciones operativas.
          </p>
        </div>

        <div class="contact__form-wrap">
          <div v-if="formSubmitted" class="contact__success">
            <div class="contact__success-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FF6A00" opacity="0.15"/><path d="M8 12.5l2.5 2.5 5.5-5.5" stroke="#FF6A00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3 class="contact__success-title">Solicitud recibida</h3>
            <p class="contact__success-msg">Te contactaremos en menos de 24 horas con una evaluación personalizada de tu activo.</p>
          </div>

          <form v-else @submit.prevent.stop="submitForm" class="contact-form" novalidate>
            <div class="contact-form__grid">
              <div class="form-field">
                <label class="form-field__label" for="name">Nombre y apellido</label>
                <input id="name" v-model="form.name" required placeholder="Juan Perez" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="email">Email</label>
                <input id="email" v-model="form.email" type="email" required placeholder="juan@email.com" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="phone">Teléfono (WhatsApp)</label>
                <input id="phone" v-model="form.phone" type="tel" required placeholder="+56 9 1234 5678" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="city">Ciudad / Comuna</label>
                <input id="city" v-model="form.city" required placeholder="Las Condes, Santiago" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="address">Dirección</label>
                <input id="address" v-model="form.address" required placeholder="Av. Apoquindo 4501" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="unit">Unidad / Depto <span class="form-field__optional">(opcional)</span></label>
                <input id="unit" v-model="form.unit" placeholder="Depto 1204" class="form-field__input" />
              </div>
              <div class="form-field">
                <label class="form-field__label" for="type">Tipo de propiedad</label>
                <select id="type" v-model="form.type" required class="form-field__input form-field__select">
                  <option value="" disabled>Selecciona</option>
                  <option value="departamento">Departamento</option>
                  <option value="casa">Casa</option>
                  <option value="habitacion">Habitación</option>
                </select>
              </div>
              <div class="form-field">
                <label class="form-field__label" for="rooms">Nº dormitorios <span class="form-field__optional">(opcional)</span></label>
                <select id="rooms" v-model="form.rooms" class="form-field__input form-field__select">
                  <option value="" disabled>Selecciona</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4+">4+</option>
                </select>
              </div>
            </div>
            <div class="form-field">
              <label class="form-field__label" for="link">Link a publicación <span class="form-field__optional">(opcional)</span></label>
              <input id="link" v-model="form.link" type="text" placeholder="https://houseandflats.com/alojamiento/816..." class="form-field__input" />
            </div>
            <div class="form-field">
              <label class="form-field__label" for="message">Mensaje <span class="form-field__optional">(opcional)</span></label>
              <textarea id="message" v-model="form.message" rows="4" placeholder="Cuéntanos sobre tu propiedad o consulta..." class="form-field__input form-field__textarea"></textarea>
            </div>
            <button
              type="button"
              @click.prevent="submitForm"
              class="btn btn--primary btn--full btn--lg form-submit"
              :disabled="submitting"
            >
              {{ submitting ? 'Enviando...' : 'Solicitar evaluación' }}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <p class="contact-form__note">No spam. Respuesta humana en menos de 24h.</p>
            <p v-if="submitError" class="contact-form__error">{{ submitError }}</p>
          </form>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <FooterNuevo />
  </div>
</template>

<script>
import axios from 'axios'
import { assets } from '@/new-constants/assets'
import santolayaLogo from '@/assets/santolaya.svg'
import HyFLoader from '@/components/Loader/HyFLoader.vue'
import FooterNuevo from '@/components/Footer/FooterNuevo.vue'

export default {
  name: 'PropertyHome',
  components: { HyFLoader, FooterNuevo },
  data() {
    return {
      assets,
      santolayaLogo,
      homeUrl: process.env.VUE_APP_SITE_URL || 'https://houseandflats.com',
      isScrolled: false,
      mobileOpen: false,
      formSubmitted: false,
      submitting: false,
      submitError: '',
      form: {
        name: '', email: '', phone: '', city: '', address: '', unit: '',
        type: '', rooms: '', link: '', message: ''
      },
      heroChecklist: [
        'Operación completa (huésped, mantenimiento, limpieza, soporte, check-in/out)',
        'Estrategia de property mixto para equilibrar rentabilidad y estabilidad',
        'Reporte financiero mensual + pago al propietario en los primeros 5 días del mes',
        'Sin honorarios fijos: cobramos vía participación alineada a resultados',
      ],
      dashboardCards: [
        {
          label: 'Ocupacion',
          value: '87%',
          trend: '+3.2%',
          iconPath: '<rect x="3" y="12" width="4" height="9" fill="#FF6A00" opacity="0.3"/><rect x="10" y="7" width="4" height="14" fill="#FF6A00" opacity="0.6"/><rect x="17" y="3" width="4" height="18" fill="#FF6A00"/>',
          special: false
        },
        {
          label: 'Ingreso mensual',
          value: '$1.240.000',
          trend: '+8.1%',
          iconPath: '<circle cx="12" cy="12" r="9" stroke="#FF6A00" stroke-width="1.5"/><path d="M12 7v1m0 8v1m-3-5h6m-5-2h4" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>',
          special: false
        },
        {
          label: 'Estancia promedio',
          value: '12 noches',
          trend: '+2.5',
          iconPath: '<rect x="3" y="5" width="18" height="16" rx="2" stroke="#FF6A00" stroke-width="1.5"/><path d="M8 3v4M16 3v4M3 10h18" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>',
          special: false
        },
        {
          label: 'Pago propietario',
          value: '$868.000',
          trend: 'Pagado a tiempo',
          iconPath: '<path d="M3 3l18 18M3 21l18-18" stroke="#FF6A00" stroke-width="1.5"/>',
          special: true
        },
      ],
      trustStats: [
        { value: '+2200', label: 'unidades operadas' },
        { value: '+75.000', label: 'noches gestionadas' },
        { value: '99%', label: 'pagos a tiempo' },
        { value: '4.8', label: 'rating promedio' },
      ],
      positioningCards: [
        {
          title: 'Estrategia de ingresos',
          description: 'Pricing, duración de estadía, mix de canales y ocupación optimizada para maximizar tu NOI(Ingreso operativo neto).',
          iconPath: '<path d="M3 17l4-8 4 4 4-6 4 4" stroke="#FF6A00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        },
        {
          title: 'Ejecución operativa',
          description: 'Gestión huésped, soporte, limpieza, mantenimiento y estándares de calidad consistentes.',
          iconPath: '<circle cx="12" cy="12" r="3" stroke="#FF6A00" stroke-width="1.5"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>',
        },
        {
          title: 'Control financiero',
          description: 'Reporte mensual detallado, trazabilidad completa y pagos programados sin sorpresas.',
          iconPath: '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#FF6A00" stroke-width="1.5"/><path d="M9 13h6M9 17h4M14 2v6h6" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>',
        },
      ],
      comparisonRows: [
        { label: 'Estabilidad de ingresos', traditional: 'Alta', shortTerm: 'Baja', hf: 'Alta', traditionalIcon: 'check', shortTermIcon: 'x', hfIcon: 'check' },
        { label: 'Optimización de precio', traditional: 'Nula', shortTerm: 'Alta', hf: 'Alta', traditionalIcon: 'x', shortTermIcon: 'check', hfIcon: 'check' },
        { label: 'Carga operativa dueño', traditional: 'Media', shortTerm: 'Alta', hf: 'Nula', traditionalIcon: 'minus', shortTermIcon: 'x', hfIcon: 'check' },
      ],
      steps: [
        { number: '01', title: 'Evaluación del activo', description: 'Analizamos zona, estándar, potencial de ingresos y condiciones del inmueble.', iconPath: '<circle cx="11" cy="11" r="8" stroke="#FF6A00" stroke-width="1.5"/><path d="M21 21l-4.35-4.35" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>' },
        { number: '02', title: 'Estrategia de renta óptima', description: 'Definimos mix de canales, pricing dinámico y duración de estadía ideal.', iconPath: '<rect x="3" y="12" width="4" height="9" fill="#FF6A00" opacity="0.3"/><rect x="10" y="7" width="4" height="14" fill="#FF6A00" opacity="0.6"/><rect x="17" y="3" width="4" height="18" fill="#FF6A00"/>' },
        { number: '03', title: 'Implementación y operación', description: 'Publicación, operación diaria, gestión de huéspedes y soporte continuo.', iconPath: '<circle cx="12" cy="12" r="10" stroke="#FF6A00" stroke-width="1.5"/><polygon points="10,8 16,12 10,16" fill="#FF6A00"/>' },
        { number: '04', title: 'Reporte y pago mensual', description: 'Liquidación detallada + pago al propietario en los primeros 5 días del mes.', iconPath: '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#FF6A00" stroke-width="1.5"/><path d="M9 13h6M9 17h4" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>' },
      ],
      metrics: [
        { label: 'Ocupación promedio', value: '85–92%', iconPath: '<rect x="3" y="12" width="4" height="9" fill="#FF6A00" opacity="0.3"/><rect x="10" y="7" width="4" height="14" fill="#FF6A00" opacity="0.6"/><rect x="17" y="3" width="4" height="18" fill="#FF6A00"/>' },
        { label: 'Duración promedio estadía', value: '10–18 noches', iconPath: '<rect x="3" y="5" width="18" height="16" rx="2" stroke="#FF6A00" stroke-width="1.5"/><path d="M8 3v4M16 3v4M3 10h18" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>' },
        { label: 'Rating huésped', value: '4.7–4.9', iconPath: '<polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="#FF6A00" stroke-width="1.5" fill="none"/>' },
        { label: 'Tiempo respuesta soporte', value: '< 30 min', iconPath: '<circle cx="12" cy="12" r="10" stroke="#FF6A00" stroke-width="1.5"/><path d="M12 6v6l4 2" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>' },
        { label: 'Pagos a propietarios a tiempo', value: '99%', iconPath: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/><polyline points="22,4 12,14.01 9,11.01" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' },
      ],
      transparencySteps: [
        'H&F cobra al huésped a través de canales (Airbnb, Booking, House and flats, corredores tradicionales, directos, etc)',
        'Se descuentan costos operativos definidos y acordados previamente',
        'Se calcula el ingreso neto con total trazabilidad',
        'El propietario recibe su porcentaje del neto + reporte detallado',
        'Pago mensual en los primeros 5 dias + reporte completo',
      ],
      netItems: [
        'Limpieza entre estadias',
        'Mantenimiento correctivo y preventivo',
        'Insumos operativos (amenities, toallas, etc.)',
        'Comisiones de canales (Airbnb, Booking)',
        'Fee de operacion H&F Property',
      ],
      testimonials: [
        {
          quote: 'Llevaba meses con mi departamento en Airbnb y la carga operativa era insostenible. H&F me libero completamente y mis ingresos mejoraron un 22%.',
          author: 'Carolina M.',
          role: 'Propietaria, 2 deptos en Santiago Centro',
        },
        {
          quote: 'Lo que mas valoro es la transparencia. Cada mes recibo un reporte claro y mi pago llega puntual. Por fin tengo control real sin operar nada.',
          author: 'Andres R.',
          role: 'Inversionista, 5 unidades en Las Condes',
        },
      ],
      // partners: ['Inmobiliaria A', 'Partner B', 'Universidad / Relocation C'],
      standards: [
        { label: 'Screening de huésped', iconPath: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#FF6A00" stroke-width="1.5"/><path d="M9 12l2 2 4-4" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' },
        { label: 'Protocolos check-in / check-out', iconPath: '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.22 2 2 0 012.22.04h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.07 6.07l1.27-.61a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#FF6A00" stroke-width="1.5"/>' },
        { label: 'Registro de incidencias', iconPath: '<path d="M9 11l3 3L22 4" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="#FF6A00" stroke-width="1.5"/>' },
        { label: 'Mantenimiento preventivo', iconPath: '<path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' },
        { label: 'Reporte mensual detallado', iconPath: '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#FF6A00" stroke-width="1.5"/><path d="M9 13h6M9 17h4" stroke="#FF6A00" stroke-width="1.5" stroke-linecap="round"/>' },
      ],
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },
    scrollTo(id) {
      this.mobileOpen = false
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    async submitForm() {
      if (this.submitting) {
        return
      }

      this.submitting = true
      this.submitError = ''

      const capacidad = this.form.rooms && this.form.rooms !== '4+'
        ? parseInt(this.form.rooms, 10)
        : null

      const payload = {
        nombre: this.form.name.trim(),
        email: this.form.email.trim(),
        telefono: this.form.phone.trim(),
        comuna: this.form.city.trim(),
        direccion: this.form.address.trim(),
        unidad_departamento: this.form.unit.trim(),
        capacidad,
        tipologia: this.form.type,
        link: this.form.link.trim(),
        mensaje: this.form.message.trim(),
      }

      try {
        await axios.post(`${process.env.VUE_APP_API_URL}notion/unidad`, payload)
        this.formSubmitted = true
        this.form = {
          name: '', email: '', phone: '', city: '', address: '', unit: '',
          type: '', rooms: '', link: '', message: ''
        }
        
      } catch (error) {
        console.error('Error al enviar la unidad a Notion:', error)
        this.submitError = 'Ocurrió un error al enviar la solicitud. Intenta nuevamente en unos minutos.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
/* ==============================
   DESIGN TOKENS & RESET
============================== */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.app {
  --orange: #FF6A00;
  --orange-dark: #e55f00;
  --orange-light: rgba(255, 106, 0, 0.1);
  --orange-faint: rgba(255, 106, 0, 0.05);
  --bg: #ffffff;
  --card: #ffffff;
  --alt-bg: #f7f7f6;
  --border: #e8e8e6;
  --text: #111110;
  --muted: #6b6b68;
  --radius: 16px;
  --radius-sm: 10px;
  --shadow: 0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
  --shadow-hover: 0 4px 20px rgba(0,0,0,0.1);
  color: var(--text);
  background: var(--bg);
  line-height: 1.6;
}

.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ==============================
   HEADER
============================== */
.header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  transition: background 0.3s, border-color 0.3s, backdrop-filter 0.3s;
}

.header--scrolled {
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 12px rgba(0,0,0,0.06);
}

.header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 18px;
  padding-bottom: 18px;
}

.logo {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}
.logo__img { height: 32px; }
.logo__property {
  font-size: 1.8rem;
  font-weight: 500;
  color: var(--text);
  line-height: 2;
  text-align: center;
  justify-content: center;
  align-items: center;
}
.logo__bold { font-size: 1.25rem; font-weight: 700; color: var(--text); letter-spacing: -0.02em; }
.logo__light { font-size: 1.25rem; font-weight: 300; color: var(--text); letter-spacing: -0.01em; }

.nav { display: flex; align-items: center; gap: 32px; }
.nav__link {
  background: none; border: none; cursor: pointer;
  font-size: 0.875rem; font-weight: 500; color: var(--muted);
  transition: color 0.2s; font-family: inherit;
}
.nav__link:hover { color: var(--text); }

.hamburger {
  display: none;
  flex-direction: column; gap: 5px;
  background: none; border: none; cursor: pointer; padding: 4px;
}
.hamburger__line {
  display: block; width: 22px; height: 2px;
  background: var(--text);
  transition: transform 0.2s, opacity 0.2s;
  border-radius: 2px;
}
.hamburger__line--open1 { transform: translateY(7px) rotate(45deg); }
.hamburger__line--open2 { opacity: 0; }
.hamburger__line--open3 { transform: translateY(-7px) rotate(-45deg); }

.mobile-menu {
  display: none;
  padding: 0 24px 24px;
  border-top: 1px solid var(--border);
  background: rgba(255,255,255,0.97);
  backdrop-filter: blur(12px);
  flex-direction: column;
  gap: 16px;
}
.mobile-menu--open { display: flex; }

/* ==============================
   BUTTONS
============================== */
.btn {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: inherit; font-weight: 600; font-size: 0.875rem;
  padding: 10px 20px; border-radius: 10px; border: none;
  cursor: pointer; transition: all 0.2s; letter-spacing: -0.01em;
  white-space: nowrap;
}
.btn--primary { background: var(--orange); color: white; }
.btn--primary:hover { background: var(--orange-dark); transform: translateY(-1px); }
.btn--outline { background: transparent; color: var(--text); border: 1.5px solid var(--border); }
.btn--outline:hover { border-color: var(--text); background: var(--alt-bg); }
.btn--lg { padding: 14px 28px; font-size: 1rem; border-radius: 12px; }
.btn--full { width: 100%; justify-content: center; }

/* ==============================
   SECTIONS
============================== */
.section { padding: 96px 0; }
.section--alt { background: var(--alt-bg); }

.section__header {
  max-width: 680px;
  margin: 0 auto 64px;
  text-align: center;
}
.section__title {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 400;
  color: var(--text);
  letter-spacing: -0.03em;
  line-height: 1.2;
  margin-bottom: 16px;
}
.section__subtitle {
  font-size: 1.0625rem;
  color: var(--muted);
  line-height: 1.7;
}
.section__cta { display: flex; justify-content: center; margin-top: 48px; }

/* ==============================
   HERO
============================== */
.hero {
  padding: 140px 0 80px;
  background: var(--bg);
}

.hero__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  margin-bottom: 64px;
}

.hero__title {
  font-size: clamp(2rem, 3.5vw, 3.25rem);
  font-weight: 400;
  color: var(--text);
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin-bottom: 24px;
}

.hero__subtitle {
  font-size: 1.0625rem;
  color: var(--muted);
  line-height: 1.7;
  margin-bottom: 32px;
}

.hero__checklist {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;
}
.hero__check-item {
  display: flex; align-items: flex-start; gap: 12px;
  font-size: 0.9rem; color: var(--muted);
}
.check-icon { width: 20px; height: 20px; flex-shrink: 0; margin-top: 2px; }

.hero__ctas { display: flex; flex-wrap: wrap; gap: 12px; }

/* Dashboard */
.hero__dashboard {
  position: relative;
}

.hero__dashboard-bg {
  position: absolute;
  bottom: -16px; right: -16px;
  width: 100%; height: 100%;
  border-radius: var(--radius);
  background: var(--orange-faint);
  z-index: -1;
}

.dashboard {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 24px;
  box-shadow: var(--shadow);
}
.dashboard__header {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 16px;
}
.dashboard__dot { width: 10px; height: 10px; border-radius: 50%; background: var(--orange-light); }

/* Santolaya floating card — positioned to the left side of the dashboard */
.santolaya-card {
  
  position: absolute;
  right: -60px;
  top: -40%; 
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px 28px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06);
  text-decoration: none;
  animation: float 3.5s ease-in-out infinite;
  transition: box-shadow 0.2s;
  z-index: 2;
}

.santolaya-card:hover {
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14), 0 4px 10px rgba(0, 0, 0, 0.08);
}

@keyframes float {
  0%   { transform: translateY(-50%) translateY(0px); }
  50%  { transform: translateY(-50%) translateY(-10px); }
  100% { transform: translateY(-50%) translateY(0px); }
}

.dashboard__logo {
  height: 120px;
  display: block;
}
.dashboard__label { font-size: 0.75rem; color: var(--muted); font-weight: 500; }
.dashboard__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

.dash-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 16px;
  display: flex; flex-direction: column; gap: 8px;
}
.dash-card__top { display: flex; align-items: center; gap: 8px; }
.dash-icon { width: 16px; height: 16px; flex-shrink: 0; }
.dash-card__label { font-size: 0.72rem; color: var(--muted); }
.dash-card__value { font-size: 1.1rem; font-weight: 700; color: var(--text); }
.dash-card__trend { font-size: 0.75rem; font-weight: 600; color: var(--orange); }
.dash-card__trend--badge {
  display: inline-flex; align-self: flex-start;
  background: #ecfdf5; color: #059669;
  padding: 2px 10px; border-radius: 20px; font-weight: 600;
}

/* Trust bar */
.trust-bar {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 32px;
  box-shadow: var(--shadow);
}
.trust-stat { text-align: center; }
.trust-stat__value { font-size: 2rem; font-weight: 700; color: var(--text); }
.trust-stat__label { font-size: 0.8rem; color: var(--muted); margin-top: 4px; }

/* ==============================
   CARDS
============================== */
.cards-grid { display: grid; gap: 24px; }
.cards-grid--3 { grid-template-columns: repeat(3, 1fr); }

.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 32px;
  box-shadow: var(--shadow);
  transition: box-shadow 0.2s, border-color 0.2s;
}
.card:hover {
  box-shadow: var(--shadow-hover);
  border-color: rgba(255,106,0,0.3);
}
.card__icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  background: var(--orange-light);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 24px;
}
.card__icon { width: 24px; height: 24px; }
.card__title { font-size: 1.125rem; font-weight: 600; color: var(--text); margin-bottom: 12px; }
.card__desc { font-size: 0.9375rem; color: var(--muted); line-height: 1.65; }

/* ==============================
   COMPARISON
============================== */
.comparison-table {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
}
.comparison-table table { width: 100%; border-collapse: collapse; }
.comparison-table thead tr { background: var(--alt-bg); border-bottom: 1px solid var(--border); }
.comparison-table th {
  padding: 18px 20px;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--muted);
}
.comparison-table th:first-child { text-align: left; }
.comparison-table__hf {
  border-left: 2px solid rgba(255,106,0,0.2);
  background: var(--orange-faint);
  color: var(--orange) !important;
  font-weight: 600 !important;
}
.comparison-table td {
  padding: 18px 20px;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
}
.comparison-table tbody tr:last-child td { border-bottom: none; }
.comparison-table__label { font-weight: 500; color: var(--text); }

.status-cell { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 0.875rem; color: var(--muted); }
.comparison-table__label .status-cell { justify-content: flex-start; }

.status-icon {
  width: 16px; height: 16px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; flex-shrink: 0;
}
.status-icon--check { background: #dcfce7; color: #16a34a; }
.status-icon--check::before { content: '✓'; }
.status-icon--x { background: #fee2e2; color: #dc2626; }
.status-icon--x::before { content: '✕'; }
.status-icon--minus { background: #f1f5f9; color: var(--muted); }
.status-icon--minus::before { content: '−'; }

.comparison-mobile { display: none; flex-direction: column; gap: 12px; }
.comparison-card {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 20px;
}
.comparison-card__label { font-size: 0.9rem; font-weight: 600; color: var(--text); margin-bottom: 12px; }
.comparison-card__cols { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; text-align: center; }
.comparison-card__col-title { font-size: 0.7rem; color: var(--muted); margin-bottom: 6px; }
.comparison-card__col-title--hf { color: var(--orange); font-weight: 600; }
.comparison-card__hf { background: var(--orange-faint); border-radius: 8px; padding: 8px; }
.comparison-card__hf .status-cell { justify-content: center; }

/* ==============================
   STEPS
============================== */
.steps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  align-items: stretch;
  position: relative;
}
.step { position: relative; display: flex; align-items: stretch; }
.step__card {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 32px;
  box-shadow: var(--shadow); flex: 1;
  transition: box-shadow 0.2s;
}
.step__card:hover { box-shadow: var(--shadow-hover); }
.step__number { font-size: 3rem; font-weight: 700; color: rgba(255,106,0,0.12); line-height: 1; }
.step__icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  background: var(--orange-light);
  display: flex; align-items: center; justify-content: center;
  margin-top: 16px; margin-bottom: 20px;
}
.step__icon { width: 24px; height: 24px; }
.step__title { font-size: 1rem; font-weight: 600; color: var(--text); margin-bottom: 8px; }
.step__desc { font-size: 0.875rem; color: var(--muted); line-height: 1.6; }

.step__connector {
  width: 24px; height: 2px;
  background: var(--border);
  align-self: center;
  flex-shrink: 0;
}

/* ==============================
   METRICS
============================== */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}
.metric-card {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 24px 16px;
  text-align: center; box-shadow: var(--shadow);
  transition: box-shadow 0.2s;
}
.metric-card:hover { box-shadow: var(--shadow-hover); }
.metric-card__icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  background: var(--orange-light);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
}
.metric-card__icon { width: 20px; height: 20px; }
.metric-card__value { font-size: 1.5rem; font-weight: 700; color: var(--text); margin-bottom: 8px; }
.metric-card__label { font-size: 0.75rem; color: var(--muted); line-height: 1.4; }
.metrics__note { text-align: center; font-size: 0.75rem; color: var(--muted); margin-top: 24px; }

/* ==============================
   TRANSPARENCY
============================== */
.transparency__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
}

.flow { display: flex; flex-direction: column; gap: 0; }
.flow__arrow { text-align: center; font-size: 1.25rem; color: var(--muted); padding: 4px 0; }

.flow-step {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-radius: var(--radius-sm);
}
.flow-step--dark { background: var(--text); color: white; }
.flow-step--muted { background: #f1f1f0; color: var(--text); border: 1px solid var(--border); }
.flow-step--accent { background: var(--orange-light); border: 2px solid rgba(255,106,0,0.3); }
.flow-step--primary { background: var(--orange); }

.flow-step__label { font-weight: 600; font-size: 0.9375rem; }
.flow-step__label--white { color: white; }
.flow-step__sub { font-size: 0.75rem; opacity: 0.65; margin-top: 2px; }
.flow-step__sub--white { color: white; opacity: 0.8; }
.flow-step__minus { font-size: 1.5rem; color: var(--muted); font-weight: 300; }
.flow-step__arrow { font-size: 1.25rem; color: white; }

.transparency__sub-title { font-size: 1.125rem; font-weight: 600; color: var(--text); margin-bottom: 24px; }

.transparency__steps { list-style: none; display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; }
.transparency__steps li { display: flex; align-items: flex-start; gap: 12px; font-size: 0.9rem; color: var(--muted); line-height: 1.5; }
.transparency__num {
  width: 20px; height: 20px; border-radius: 50%;
  background: var(--orange-light); color: var(--orange);
  font-size: 0.7rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-top: 2px;
}

.net-box {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 24px;
}
.net-box__title { font-size: 0.875rem; font-weight: 600; color: var(--text); margin-bottom: 6px; }
.net-box__sub { font-size: 0.75rem; color: var(--muted); margin-bottom: 16px; }
.net-box__list { list-style: none; display: flex; flex-direction: column; gap: 8px; }
.net-box__list li { display: flex; align-items: center; gap: 10px; font-size: 0.875rem; color: var(--muted); }
.net-box__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--orange); flex-shrink: 0; }

/* ==============================
   SOCIAL PROOF
============================== */
.testimonials { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
.testimonial {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 32px; box-shadow: var(--shadow);
}
.stars { display: flex; gap: 4px; margin-bottom: 16px; }
.testimonial__quote { font-size: 0.9375rem; color: var(--text); line-height: 1.7; margin-bottom: 24px; font-style: italic; }
.testimonial__footer { border-top: 1px solid var(--border); padding-top: 16px; }
.testimonial__author { font-size: 0.875rem; font-weight: 600; color: var(--text); }
.testimonial__role { font-size: 0.75rem; color: var(--muted); margin-top: 2px; }

.partners { display: flex; flex-wrap: wrap; justify-content: center; gap: 16px; margin-bottom: 64px; }
.partner-badge {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 10px 20px;
  font-size: 0.875rem; font-weight: 500; color: var(--muted);
}

.standards__title { text-align: center; font-size: 1.0625rem; font-weight: 600; color: var(--text); margin-bottom: 32px; }
.standards__grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.standard-item {
  display: flex; align-items: center; gap: 12px;
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 16px;
  font-size: 0.8125rem; color: var(--text);
}
.standard-item__icon { width: 20px; height: 20px; flex-shrink: 0; }

/* ==============================
   CONTACT
============================== */
.contact__form-wrap { max-width: 680px; margin: 0 auto; }

.contact__success {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 64px 32px;
  text-align: center; box-shadow: var(--shadow);
}
.contact__success-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--orange-light);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px;
}
.contact__success-title { font-size: 1.25rem; font-weight: 600; color: var(--text); margin-bottom: 8px; }
.contact__success-msg { font-size: 0.9375rem; color: var(--muted); }

.contact-form {
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 40px; box-shadow: var(--shadow);
}
.contact-form__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }

.form-field { display: flex; flex-direction: column; gap: 6px; }
.form-field__label { font-size: 0.875rem; font-weight: 500; color: var(--text); }
.form-field__optional { color: var(--muted); font-weight: 400; }
.form-field__input {
  width: 100%;
  padding: 11px 14px;
  background: var(--bg); border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  font-family: inherit; font-size: 0.9375rem; color: var(--text);
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}
.form-field__input:focus { border-color: var(--orange); box-shadow: 0 0 0 3px rgba(255,106,0,0.1); }
.form-field__input::placeholder { color: #bbb; }
.form-field__select { appearance: none; cursor: pointer; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b6b68' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 40px; }
.form-field__textarea { resize: none; min-height: 100px; }

.form-submit { margin-top: 12px; }
.contact-form__note { text-align: center; font-size: 0.75rem; color: var(--muted); margin-top: 12px; }
.contact-form__error {
  margin-top: 8px;
  text-align: center;
  font-size: 0.8rem;
  color: #dc2626;
}

/* ==============================
   LOADING OVERLAY
============================== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 17, 16, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-overlay__content {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-overlay__spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid rgba(255, 106, 0, 0.2);
  border-top-color: #ff6a00;
  animation: loading-spin 0.8s linear infinite;
}

.loading-overlay__text {
  font-size: 0.95rem;
  color: var(--text);
}

@keyframes loading-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ==============================
   FOOTER
============================== */
.footer {
  background: var(--card);
  border-top: 1px solid var(--border);
  padding: 48px 0;
}
.footer__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; flex-wrap: wrap; gap: 16px; }
.footer__links { display: flex; align-items: center; gap: 20px; }
.footer__link { display: inline-flex; align-items: center; gap: 8px; font-size: 0.875rem; color: var(--muted); text-decoration: none; transition: color 0.2s; }
.footer__link:hover { color: var(--text); }
.footer__logo-santolaya { height: 45px; }

.footer__social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: opacity 0.2s, transform 0.2s;
  text-decoration: none;
}
.footer__social-link:hover {
  opacity: 0.85;
  transform: translateY(-2px);
}

.footer__bottom {
  border-top: 1px solid var(--border); padding-top: 24px;
  text-align: center; font-size: 0.75rem; color: var(--muted); line-height: 1.6;
}

/* ==============================
   RESPONSIVE
============================== */
@media (max-width: 1024px) {
  .metrics-grid { grid-template-columns: repeat(3, 1fr); }
  .standards__grid { grid-template-columns: repeat(3, 1fr); }
  .steps-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .step__connector { display: none; }
}

@media (max-width: 768px) {
  .hero { padding: 100px 0 60px; }
  .hero__inner { grid-template-columns: 1fr; gap: 48px; }
  .hero__dashboard { display: none; }
  .trust-bar { grid-template-columns: repeat(2, 1fr); }
  .nav { display: none; }
  .hamburger { display: flex; }
  .cards-grid--3 { grid-template-columns: 1fr; }
  .comparison-table { display: none; }
  .comparison-mobile { display: flex; }
  .testimonials { grid-template-columns: 1fr; }
  .transparency__grid { grid-template-columns: 1fr; gap: 40px; }
  .steps-grid { grid-template-columns: 1fr; }
  .metrics-grid { grid-template-columns: repeat(2, 1fr); }
  .standards__grid { grid-template-columns: repeat(2, 1fr); }
  .contact-form__grid { grid-template-columns: 1fr; }
  .contact-form { padding: 28px 20px; }
}

@media (max-width: 480px) {
  .trust-bar { grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 20px; }
  .metrics-grid { grid-template-columns: 1fr 1fr; }
  .standards__grid { grid-template-columns: 1fr; }
  .section { padding: 64px 0; }
}
</style>